/*
  NBV2 Devices (sections/nbv2-devices.liquid)

  Every device panel, its close button and its arrow pair are already in the
  markup -- this script only decides which panel is visible and whether the
  drawer is open. Nothing is fetched, cloned or constructed here, so a marker
  is an inert button until this file lands rather than a broken one.

  Listeners are delegated from the two containers, which is why the per-device
  copies of the close button and the arrows cost nothing.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nbv2-devices]';
  var BODY_LOCK_CLASS = 'overflow-hidden';

  /* Must outlast the panel's own transition (0.4s in the CSS). */
  var CLOSE_FALLBACK_MS = 600;

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function init(root) {
    if (root.nbv2DevicesInit === true) {
      return;
    }

    var media = root.querySelector('[data-nbv2-dev-media]');
    var drawer = root.querySelector('[data-nbv2-dev-drawer]');
    var panelRoot = root.querySelector('[data-nbv2-dev-panel-root]');
    var hotspots = Array.prototype.slice.call(root.querySelectorAll('[data-nbv2-dev-hotspot]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-nbv2-dev-panel]'));

    if (!media || !drawer || !panelRoot || hotspots.length === 0 || panels.length === 0) {
      return;
    }

    root.nbv2DevicesInit = true;

    var active = -1;
    var opener = null;
    var closeTimer = null;

    function activePanel() {
      return active > -1 ? panels[active] : null;
    }

    /*
      Focus is trapped on the live device, not on the dialog: Dawn's
      getFocusableElements (assets/global.js:216) walks [hidden] subtrees too,
      so trapping the dialog would hand Tab the buttons of every other panel.
    */
    function trap() {
      var panel = activePanel();
      if (!panel || typeof window.trapFocus !== 'function') {
        return;
      }
      window.trapFocus(panel, panel.querySelector('[data-nbv2-dev-close]'));
    }

    function release(focusTarget) {
      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(focusTarget || null);
      } else if (focusTarget) {
        focusTarget.focus();
      }
    }

    function setActive(index) {
      active = index;

      panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });

      hotspots.forEach(function (hotspot, i) {
        var isActive = i === index;
        hotspot.classList.toggle('is-active', isActive);
        hotspot.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });

      /*
        Naming the device on the dialog rather than pointing aria-labelledby at
        a title: the titles are per-panel, so a shared id would have to be moved
        around and would briefly be duplicated.
      */
      var panel = activePanel();
      var title = panel ? panel.querySelector('.nbv2-devices__device-title') : null;
      if (title && title.textContent.trim() !== '') {
        panelRoot.setAttribute('aria-label', title.textContent.trim());
      }
    }

    function isOpen() {
      return drawer.hidden === false;
    }

    function open(index, trigger) {
      /*
        Reopening mid-close: the pending transitionend would otherwise fire on
        the way *in* and hide the drawer we are busy showing.
      */
      if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      drawer.removeEventListener('transitionend', onTransitionEnd);

      opener = trigger || null;
      setActive(index);
      drawer.hidden = false;
      document.body.classList.add(BODY_LOCK_CLASS);

      /*
        The panel has to be laid out at translateX(100%) for one frame before
        .is-open lands, or the browser has nothing to transition from.
      */
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () {
          drawer.classList.add('is-open');
        });
      } else {
        drawer.classList.add('is-open');
      }

      trap();
      document.addEventListener('keydown', onKeydown);
    }

    function finishClose() {
      closeTimer = null;
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', onTransitionEnd);
      setActive(-1);
    }

    function onTransitionEnd(event) {
      /* Only the panel's own slide counts -- the overlay fade ends alongside it. */
      if (event.target !== panelRoot || event.propertyName !== 'transform') {
        return;
      }
      if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
      }
      finishClose();
    }

    function close() {
      if (!isOpen()) {
        return;
      }

      document.removeEventListener('keydown', onKeydown);
      drawer.classList.remove('is-open');
      document.body.classList.remove(BODY_LOCK_CLASS);
      release(opener);
      opener = null;

      if (prefersReducedMotion()) {
        finishClose();
        return;
      }

      /* transitionend can be skipped entirely on a hidden tab; always time out. */
      drawer.addEventListener('transitionend', onTransitionEnd);
      closeTimer = window.setTimeout(finishClose, CLOSE_FALLBACK_MS);
    }

    function step(delta) {
      if (active < 0) {
        return;
      }
      var count = panels.length;
      setActive((active + delta + count) % count);
      trap();
    }

    function onMediaClick(event) {
      var hotspot = event.target.closest
        ? event.target.closest('[data-nbv2-dev-hotspot]')
        : null;
      if (!hotspot) {
        return;
      }

      var index = parseInt(hotspot.getAttribute('data-index'), 10);
      if (isNaN(index) || !panels[index]) {
        return;
      }

      open(index, hotspot);
    }

    function onDrawerClick(event) {
      var target = event.target;

      if (target === event.currentTarget || (target.closest && target.closest('[data-nbv2-dev-overlay]'))) {
        close();
        return;
      }

      if (!target.closest) {
        return;
      }

      if (target.closest('[data-nbv2-dev-close]')) {
        close();
        return;
      }

      if (target.closest('[data-nbv2-dev-prev]')) {
        step(-1);
        /*
          setActive swapped in a different panel, so the arrow that was clicked
          is now hidden and focus would fall to <body>. Re-aim it at the
          equivalent arrow in the panel that just became live.
        */
        refocus('[data-nbv2-dev-prev]');
        return;
      }

      if (target.closest('[data-nbv2-dev-next]')) {
        step(1);
        refocus('[data-nbv2-dev-next]');
      }
    }

    function refocus(selector) {
      var panel = activePanel();
      var button = panel ? panel.querySelector(selector) : null;
      if (button) {
        button.focus();
      }
    }

    function onKeydown(event) {
      if (event.key !== 'Escape' && event.keyCode !== 27) {
        return;
      }
      close();
    }

    media.addEventListener('click', onMediaClick);
    drawer.addEventListener('click', onDrawerClick);

    root.nbv2DevicesDestroy = function () {
      if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      media.removeEventListener('click', onMediaClick);
      drawer.removeEventListener('click', onDrawerClick);
      drawer.removeEventListener('transitionend', onTransitionEnd);
      document.removeEventListener('keydown', onKeydown);

      /* A section can be unloaded mid-open; do not leave the page unscrollable. */
      if (isOpen()) {
        document.body.classList.remove(BODY_LOCK_CLASS);
        release(null);
      }

      root.nbv2DevicesInit = false;
      root.nbv2DevicesDestroy = null;
    };
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));

    if (container.nodeType === 1 && container.matches && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }

    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = Array.prototype.slice.call(event.target.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(function (root) {
      if (typeof root.nbv2DevicesDestroy === 'function') {
        root.nbv2DevicesDestroy();
      }
    });
  });
})();
